(() => {
  const instances = new Map();
  const selector = "[data-monarch-butterfly-footer]";

  function mount(root) {
    if (instances.has(root)) return;
    const button = root.querySelector("[data-mbf-toggle]");
    const buttonLabel = root.querySelector("[data-mbf-toggle-label]");
    const art = root.querySelector("[data-mbf-art]");
    const artStage = root.querySelector("[data-mbf-reveal-art]");
    const controller = new AbortController();
    const originalHidden = root.hidden;
    const gsap = window.gsap;
    const ScrollTrigger = window.ScrollTrigger;
    let media;
    let journeyObserver;
    let journey;
    let ambient;
    let entrance;
    let inView = false;
    let userPaused = false;
    let motionEnabled = false;
    let revealed = false;
    let alive = true;
    let refreshFrame = 0;

    const on = (target, name, callback, options = {}) => {
      target?.addEventListener(name, callback, {
        ...options,
        signal: controller.signal,
      });
    };

    const refresh = () => {
      if (!ScrollTrigger || refreshFrame) return;
      refreshFrame = requestAnimationFrame(() => {
        refreshFrame = 0;
        if (alive) ScrollTrigger.refresh();
      });
    };

    const syncPlayback = () => {
      const playing =
        motionEnabled &&
        inView &&
        !document.hidden &&
        !userPaused &&
        !root.hidden;
      ambient?.paused(!playing);
      if (entrance && entrance.progress() < 1) entrance.paused(!playing);
      root.dataset.mbfState = motionEnabled
        ? playing
          ? "playing"
          : "paused"
        : "static";
      if (button) {
        button.hidden = !motionEnabled;
        button.setAttribute("aria-pressed", String(userPaused));
        if (buttonLabel)
          buttonLabel.textContent = userPaused
            ? button.dataset.resumeLabel
            : button.dataset.pauseLabel;
      }
    };

    const syncJourney = () => {
      journeyObserver?.disconnect();
      journey = document.querySelector(
        "[data-monarch-journey][data-mj-mode='scroll']",
      );
      const update = () => {
        const complete =
          journey?.dataset.mjComplete === "true" ||
          journey?.dataset.view === "complete" ||
          journey?.querySelector("[data-mj-form-state='success']");
        const nextHidden = originalHidden || Boolean(complete);
        if (root.hidden !== nextHidden) {
          root.hidden = nextHidden;
          refresh();
        }
        syncPlayback();
      };
      if (journey) {
        journeyObserver = new MutationObserver(update);
        journeyObserver.observe(journey, {
          attributes: true,
          attributeFilter: ["data-mj-complete", "data-view"],
        });
        const formState = journey.querySelector("[data-mj-form-state]");
        if (formState)
          journeyObserver.observe(formState, {
            attributes: true,
            attributeFilter: ["data-mj-form-state"],
          });
      }
      update();
    };

    on(button, "click", () => {
      userPaused = !userPaused;
      syncPlayback();
    });
    on(document, "visibilitychange", syncPlayback);
    on(root.querySelector("[data-mbf-cta]"), "click", (event) => {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
        return;
      const inquiry = document.getElementById("mj-inquiry");
      if (!inquiry || inquiry.hidden) {
        event.currentTarget.setAttribute("href", "/pages/train#walkthrough");
      } else {
        event.currentTarget.setAttribute("href", "#mj-inquiry");
      }
    });

    syncJourney();
    if (gsap && art && artStage && root.dataset.mbfMotion !== "false") {
      if (ScrollTrigger) gsap.registerPlugin(ScrollTrigger);
      media = gsap.matchMedia();
      media.add(
        {
          desktop: "(min-width: 960px)",
          mobile: "(max-width: 959px)",
          motion: "(prefers-reduced-motion: no-preference)",
        },
        (context) => {
          if (!context.conditions.motion) {
            motionEnabled = false;
            syncPlayback();
            return;
          }
          motionEnabled = true;
          const desktop = context.conditions.desktop;
          ambient = gsap.timeline({
            paused: true,
            repeat: -1,
            yoyo: true,
            defaults: { duration: 5, ease: "sine.inOut" },
          });
          ambient.to(
            art,
            { y: desktop ? -12 : -7, rotation: desktop ? 0.8 : 0.45 },
            0,
          );
          [
            ["[data-mbf-wing-left]", -1, "100% 55%"],
            ["[data-mbf-wing-right]", 1, "0% 55%"],
          ].forEach(([wingSelector, direction, origin]) => {
            const wing = root.querySelector(wingSelector);
            if (!wing) return;
            const pivot = wing.dataset.mbfSvgOrigin
              ? { svgOrigin: wing.dataset.mbfSvgOrigin }
              : { transformOrigin: wing.dataset.mbfOrigin || origin };
            gsap.set(wing, pivot);
            ambient.to(
              wing,
              { rotation: direction * (desktop ? 2.4 : 1.6), scaleX: 0.985 },
              0,
            );
          });
          if (!revealed) {
            entrance = gsap.fromTo(
              artStage,
              { y: 25, scale: 0.97, opacity: 0.5 },
              {
                y: 0,
                scale: 1,
                opacity: 1,
                duration: 1.25,
                ease: "power2.out",
                paused: true,
                immediateRender: false,
                onComplete: () => {
                  revealed = true;
                },
              },
            );
          }
          let observer;
          if (ScrollTrigger) {
            const trigger = ScrollTrigger.create({
              trigger: root,
              start: "top bottom",
              end: "bottom top",
              onToggle: (self) => {
                inView = self.isActive;
                syncPlayback();
              },
              onRefresh: (self) => {
                inView = self.isActive;
                syncPlayback();
              },
            });
            inView = trigger.isActive;
          } else if (window.IntersectionObserver) {
            observer = new IntersectionObserver((entries) => {
              inView = entries.some((entry) => entry.isIntersecting);
              syncPlayback();
            });
            observer.observe(root);
          } else {
            inView = true;
          }
          syncPlayback();
          return () => {
            observer?.disconnect();
            ambient = null;
            entrance = null;
            motionEnabled = false;
            inView = false;
            syncPlayback();
          };
        },
        root,
      );
      root.querySelectorAll("img").forEach((image) => {
        if (!image.complete) on(image, "load", refresh, { once: true });
      });
      document.fonts?.ready.then(() => {
        if (alive) refresh();
      });
    }

    instances.set(root, {
      syncJourney,
      destroy() {
        alive = false;
        controller.abort();
        journeyObserver?.disconnect();
        media?.revert();
        if (refreshFrame) cancelAnimationFrame(refreshFrame);
        root.hidden = originalHidden;
        delete root.dataset.mbfState;
        if (button) button.hidden = true;
      },
    });
  }

  const initialize = (scope = document) => {
    if (scope.matches?.(selector)) mount(scope);
    scope.querySelectorAll(selector).forEach(mount);
    instances.forEach((instance) => instance.syncJourney());
  };
  initialize();
  document.addEventListener("shopify:section:load", (event) =>
    initialize(event.target),
  );
  document.addEventListener("shopify:section:unload", (event) => {
    for (const [root, instance] of instances) {
      if (event.target.contains(root)) {
        instance.destroy();
        instances.delete(root);
      }
    }
  });
})();
